/**
 * @module lib/analytics
 * @description Set of utilities to work with analytics
 * @author Srinaath Ravichandran <sravichandran@deloitte.com>
 */

import Cookies from "js-cookie";
import qs from "query-string";
import url from "url";
import { captureException, log } from "@lululemon/mwa-utils";
import Util from "./util";

// HELPERS
import { analyzePdpUrlPath } from "../util/helpers";

import {
  trackEvent as mwaAnalyticsTrackEvent,
  EVENT_CATEGORY,
  EVENT_TYPE,
} from "@lululemon/mwa-analytics";

const util = new Util("analytics.js");
const $window = util.windowEvent();

export function trackEvent(event, values) {
  try {
    // eslint-disable-next-line no-underscore-dangle
    $window?._satellite?.track(event, values);
  } catch (e) {
    captureException(e, "error", { tags: { isThirdParty: true } });
  }
}

// https://docs.adobe.com/content/help/en/target/using/implement-target/client-side/functions-overview/adobe-target-trackevent.html
export function trackTargetEvent(options) {
  try {
    $window?.adobe?.target?.trackEvent(options);
  } catch (e) {
    captureException(e, "error", { tags: { isThirdParty: true } });
  }
}

export const parseToNumber = (n) =>
  typeof n === "string" ? parseInt(n.replace("$", ""), 10) : n;

let slCookie = null;

function getSlCookie() {
  if (slCookie !== null) return slCookie;
  slCookie = Cookies.get("sl");
  return slCookie;
}

export const getLocalePrefix = (productInfo) =>
  (productInfo && productInfo.currencyCode === "CAD") || getSlCookie() === "CA"
    ? "ca_"
    : "us_";

/**
 * @function
 */
export function updatePageSpecificAnalytics() {
  updatePage();
}

/**
 * @function
 */
export function initAnalyticsObject(country = "US", geoRegion = "US") {
  return {
    page: {
      pageInfo: {
        country,
        geoRegion,
      },
      attributes: {
        internalSearch: {
          term: "",
          results: "",
        },
      },
      category: {
        pageType: "",
      },
    },
    user: [
      {
        userState: "guest",
        segment: { activeMember: false },
      },
    ],
    cart: {},
    product: [
      {
        attributes: {},
      },
    ],
  };
}

export const triggerViewMoreButtonAnalytics = ({
  pageType,
  componentType = "Button",
}) => {
  try {
    mwaAnalyticsTrackEvent(EVENT_CATEGORY.COMPONENT_EVENT, {
      eventSubType: EVENT_TYPE.INTERACTION,
      component: {
        id: `${pageType}:view-more-products`,
        type: componentType, // "" if N/A
        text: "View More Products", // "" if N/A
        position: -1,
        totalCount: -1,
      },
    });
  } catch (error) {
    console.error(error);
  }
};

export function getPDPGender(pageData) {
  if (
    pageData["product-summary"].gender &&
    pageData["product-summary"].gender.includes(",")
  ) {
    return "all";
  }
  // checks to see if its on a GIRL page, girl does not have product-summary gender
  const isGirl =
    pageData["refinement-crumbs"].ancestors[0]["navigation-state"].includes(
      "girl"
    );
  if (isGirl) {
    return "girls";
  }
  return pageData["product-summary"].gender || "";
}

export function getCDPGender(pageData) {
  const categoryRefinementCrumb = pageData?.refinementCrumbs.find(
    (crumb) => crumb["dimension-name"] === "product.category"
  );
  let gender;
  if (categoryRefinementCrumb?.ancestors?.length > 0) {
    gender = categoryRefinementCrumb.ancestors[0].properties["unified-id"];
  } else if (pageData?.["refinement-crumbs"]) {
    gender = pageData["refinement-crumbs"]?.ancestors?.[0]?.label;
  } else {
    gender = categoryRefinementCrumb?.properties?.["unified-id"];
  }
  gender = gender?.toLowerCase();
  if (gender !== "men" && gender !== "women" && gender !== "girls") {
    gender = "all";
  }
  return gender;
}

export function getAppliedFilter(pageData) {
  const array = [];
  pageData.refinementCrumbs.map((crumb) => {
    if (crumb["dimension-name"] !== "product.category") {
      if (crumb["dimension-name"] === "product.markDown") {
        array.push("WMTM");
      } else if (crumb["dimension-name"] === "product.whatsNew") {
        array.push("whats new");
      } else {
        array.push(crumb.label.split("|")[0]);
      }
    }
  });

  return array;
}

// Stubbing it out. Once Country selector is integrated this object needs to populated
// pageInfo: {
//   country: 'ca',
//   region: 'ca'
// }
/**
 * @function
 */
export function updatePage() {
  if ($window.digitalData) {
    const pageInfo = {};
    pageInfo.country = Cookies.get("Country") || "";
    pageInfo.geoRegion = Cookies.get("sl") || "";
    $window.digitalData.page.pageInfo = pageInfo;
  }
}

export function setEvar(eVarName, value) {
  if ($window && $window._satellite) {
    try {
      // target-A4L is Analytics Bucket for the DAR App for Storing eVars
      // All eVars must be stored in this bucket NSBN-2587
      trackEvent("target-A4L", { [eVarName]: value });
    } catch (e) {
      _satellite.logger.log(e, "error message");
    }
  }
}

/**
 * @function
 */
export function updateErrorPage(errorPageType, errorDesc) {
  let errorObj = {
    errorType: "maintenance-page",
    errorMessage: "this is awkward… our site is taking a savasana.",
  };

  if (errorPageType && errorDesc) {
    errorObj = {
      errorType: errorPageType,
      errorMessage: errorDesc,
    };
  }

  if ($window.digitalData) {
    $window.digitalData.error = errorObj;
  }
}

/**
 * @function
 */
export function addError(errorType, errorMessage) {
  if ($window.digitalData) {
    if (!errorType) {
      return;
    }

    if (!errorMessage) {
      return;
    }

    if (!$window.digitalData.error) {
      $window.digitalData.error = {};
    }

    // encode character entities and strip HTML from the error message
    $window.digitalData.error.errorType = errorType;
    $window.digitalData.error.errorMessage = errorMessage;
    trackEvent("error-tracking");
  }
}

export function clearError() {
  if ($window.digitalData && $window.digitalData.error) {
    // clear the error object in digitalData
    $window.digitalData.error = {
      ...$window.digitalData.error,
      ...{
        errorType: "",
        errorMessage: "",
      },
    };
  }
}

export const getAnalyticsTextFormat = (string) => {
  try {
    if (typeof string !== "string") return "";
    return string.toLowerCase().replace(/ /g, "-");
  } catch (e) {
    log.error(`#getAnalyticsTextFormat ${e}`);
  }
};

export function getCountrySkuId(product, skuStyle = {}) {
  const countryPrefix = getLocalePrefix(product);
  const sku =
    skuStyle.sku ||
    product.skuId ||
    (product["sku-style-order"] &&
      product["sku-style-order"][0] &&
      product["sku-style-order"][0].sku);
  const hasCountryPrefix = /_/.test(sku);
  return hasCountryPrefix || !sku ? sku : `${countryPrefix}${sku}`;
}

export const formatNameTracking = (name) =>
  encodeURIComponent(name)
    .replace(/'/g, "%27")
    .replace(/\(/g, "%28")
    .replace(/\./g, "%2E")
    .replace(/-/g, "%2D")
    .replace(/\)/g, "%29")
    .replace(/\*/g, "%2A");

const formatDisplayType = (swatchIndex, isLabel) => {
  if (swatchIndex) {
    return "swatch";
  }
  if (isLabel) {
    return "label";
  }
  return "tile";
};

const formatFacetID = (name = "", accordionName = "", isFacet = false) => {
  const ID = isFacet
    ? `facets-section-${accordionName}-${name}`
    : `facets-section-${name}-${accordionName}`;
  return ID.replace(/[./&+:$]/g, "")
    .replace(/ /g, "-")
    .replace("--", "-")
    .toLowerCase();
};

export const formatTruncatedButtonTracking = (
  buttonName,
  position,
  accordionName
) => {
  let filterButtonAttributes = {};
  const formattedData = JSON.stringify({
    type: "button",
    button: {
      type: "accordion",
      name: formatNameTracking(buttonName),
      id: formatFacetID(buttonName, accordionName, false),
      position,
    },
    attributes: {
      component: "filters-facets",
      displayType: "label",
    },
    version: "1.0",
  });
  filterButtonAttributes = {
    "data-lulu-track": "facets-section-view",
    "data-lulu-attributes": formattedData,
  };
  return filterButtonAttributes;
};

export const formatFilterTracking = (
  facetName,
  position,
  isCurrentHidden,
  isDefaultHidden,
  accordionName,
  isMultiSelect
) => {
  let filterAttributes = {};
  const formattedData = JSON.stringify({
    type: "filter",
    filter: {
      type: "facet",
      name: formatNameTracking(facetName),
      id: formatFacetID(facetName, accordionName, true),
      position: position + 1,
      group: formatNameTracking(accordionName),
    },
    attributes: {
      component: "filters-facets",
      displayType: isMultiSelect ? "checkbox" : "label",
      viewMore: {
        stateDefault: isDefaultHidden,
        stateCurrent: isCurrentHidden,
      },
    },
    version: "1.0",
  });
  filterAttributes = {
    "data-lulu-track": "facets-view",
    "data-lulu-attributes": formattedData,
  };
  return filterAttributes;
};

// function to join both Product Badges and Product Additional Info (product Detail)
export function joinBadges(badge1, badge2) {
  const nonEmptyStrings = [badge1, badge2].filter((str) => !!str);
  return nonEmptyStrings.length > 0 ? nonEmptyStrings.join(":") : undefined;
}

const formatProductSwatchTracking = (
  item,
  skuStyle,
  isBOPIS,
  productIndex,
  isLabel,
  swatchIndex,
  colorID,
  productID,
  skuId,
  categoryUnifiedId = null,
  overrides,
  productBadges,
  productDetail
) =>
  JSON.stringify({
    type: "product",
    product: {
      name: formatNameTracking(item["display-name"]),
      skuID: skuId || overrides?.skuID || "",
      productID,
      categoryUnifiedID:
        item["unified-id"]?.toLowerCase() || categoryUnifiedId || "",
      styleID: overrides?.styleID,
      color: swatchIndex ? colorID : skuStyle["color-id"],
      size: overrides?.size,
      deliveryType: isBOPIS ? "pickup" : "ship",
      quantity: overrides?.quantity,
      price: parseToNumber(overrides?.price),
    },
    attributes: {
      component: overrides?.component || "product-list",
      displayType: formatDisplayType(swatchIndex, isLabel),
      position: swatchIndex ? `${productIndex}-${swatchIndex}` : productIndex,
      badges: joinBadges(productBadges, productDetail),
    },
    version: overrides?.version || "2.1",
  });

/**
 * Returns analytics data attributes for AEM product tiles and color swatches.
 *
 * For product tiles, returns values for 'data-skuid', 'data-productid', and 'data-categoryunifiedid.
 * For color swatches only, also returns values for 'data-lulu-track' and 'data-lulu-attributes'.
 *
 * @returns {Object}
 */
// eslint-disable-next-line consistent-return
export function analyticsProductLinkDataAttributes({
  product,
  skuStyle = {},
  isBOPIS = false,
  productIndex = null,
  isLabel = false,
  swatchIndex = null,
  colorID = null,
  overrides = {},
  productBadges = "",
  productDetail = "",
}) {
  try {
    if (!product) {
      return {};
    }

    let tileAndSwatchAttrs = {};

    const {
      "repository-id": repoDashId,
      id,
      "pdp-url": pdpDashUrl,
      pdpUrl,
    } = product;

    const productId = overrides?.productId || repoDashId || id;
    const pdpPathname = url.parse(pdpDashUrl || pdpUrl || "").pathname;
    const categoryUnifiedId = analyzePdpUrlPath(pdpPathname).category;
    const skuId = getCountrySkuId(product, skuStyle);

    // SCOPE THIS TO PRODUCT TILES AND SWATCHES ONLY
    if (productIndex || swatchIndex) {
      const formattedTracking = formatProductSwatchTracking(
        product,
        skuStyle,
        isBOPIS,
        productIndex,
        isLabel,
        swatchIndex,
        colorID,
        productId,
        skuId,
        categoryUnifiedId,
        overrides,
        productBadges,
        productDetail
      );
      tileAndSwatchAttrs = {
        "data-lulu-track": overrides?.dataLuluTrack || "pdp-link",
        "data-lulu-attributes": formattedTracking,
      };
    }

    return {
      "data-skuid": skuId,
      "data-productid": productId,
      "data-categoryunifiedid": categoryUnifiedId,
      ...tileAndSwatchAttrs,
    };
  } catch (e) {
    log.error(`#analyticsProductLinkDataAttributes Error: ${e}`);
  }
}

export const clearAppliedFilter = () => {
  if ($window?.digitalData?.page?.attributes?.appliedFilters?.length) {
    $window.digitalData.page.attributes.appliedFilters.length = 0;
  }
};

/**
 * Returns a value to populate 'data-lulu-attributes' on AEM product tiles on Story pages.
 *
 * @deprecated
 * @returns {Object}
 */
export const getProductAnalytics = (product, skuStyle, index, overrides) => {
  try {
    if (!product) return {};

    const sku = skuStyle || product["sku-style-order"]?.[0];
    const isUSA = product.currencyCode === "USD";
    const skuID =
      sku?.sku ||
      (isUSA ? `us_0${product.skuId}` : `ca_0${product.skuId}`) ||
      "";
    const color = sku?.["color-id"] || product.color || "";
    const styleIdWithColorCode =
      sku?.["style-id"] || qs.parse(product["pdp-url"].split("?")[1]).color;
    const styleID = styleIdWithColorCode?.split("-")[0] || "";
    const categoryUnifiedID = product["pdp-url"].split("/")[2] || "";
    const listPrice = product["list-price"] || product.listPrice;
    const listPriceString = Array.isArray(listPrice)
      ? listPrice?.[0]
      : listPrice;
    const salePrice = parseToNumber(product?.salePrice);
    const price = salePrice || parseToNumber(listPriceString) || "";
    const position = index + 1 || "";

    return {
      type: "product",
      product: {
        name: formatNameTracking(product["display-name"]),
        skuID: overrides?.skuID || skuID,
        productID: overrides?.productId,
        categoryUnifiedID,
        color: overrides?.color || color,
        styleID,
        size: "",
        deliveryType: "ship",
        quantity: "",
        price,
      },
      attributes: {
        component: overrides?.component,
        displayType: overrides?.displayType,
        position,
      },
      version: overrides?.version || 2.2,
    };
  } catch (e) {
    log.error(`#getMembershipAnalytics Error: ${e}`);
  }
};

// Updates CDP window.digitalData.product color and skuId when a color swatch is selected
export function updateProductColorAndSkuId(newProductColorData) {
  if ($window.digitalData) {
    const { product: products } = $window.digitalData;
    const { color, skuId, size, name } = newProductColorData;
    const updatedProducts = products.map((product) => {
      if (product.productInfo.name === name) {
        product.productInfo = {
          ...product.productInfo,
          color,
          skuId,
          size,
        };
      }
      return product;
    });
    $window.digitalData.product = updatedProducts;
  }
}

export const formatRvRecosTracking = (product) => {
  let rvRecosTracking = {};
  const isUSA = product["currency-code"] === "USD";
  const skuIdProduct = product.id?.split("_")[0] || "";
  const skuId = isUSA ? `us_${skuIdProduct}` : `ca_${skuIdProduct}`;
  const styleSku = product["sku-style-order"]?.[0];
  const styleId = styleSku?.["style-id"].split("-")[0] || "";
  const priceProd = product["list-price"]?.[0] || "";
  rvRecosTracking = {
    priceProd,
    skuId,
    styleId,
  };
  return rvRecosTracking;
};

/**
 * Returns a value to populate data attributes for product tiles sourced from GraphQL.
 *
 * @param {Object} product
 * @param {Object} product.defaultSku - Default SKU for the product.
 * @param {string} product.href - The URL of the product's PDP.
 * @param {string} product.id - The product ID.
 * @param {string} product.name - The name of the product.
 * @param {Number} index - The relative position of the link based on the list.
 * @param {Object} overrides - Overrides for the default values.
 *
 * @returns {Object}
 */
export const productTileAnalyticsDataAttributes = ({
  product,
  index,
  overrides = {},
}) => {
  try {
    if (!product) return {};

    const { component, displayType } = overrides;

    const { defaultSku = {}, href, id: productID, name } = product;

    const isUSA = defaultSku.price.currency.code === "USD";
    const skuID = isUSA ? `us_${defaultSku.id}` : `ca_${defaultSku.id}`;
    const color = defaultSku.color.code;
    const categoryUnifiedID = href.split("/")[2] || "";
    const listPrice = parseToNumber(defaultSku.price.listPrice);
    const salePrice = parseToNumber(defaultSku.price.salePrice);
    const price = salePrice || listPrice || "";
    const position = index + 1 || "";

    return {
      "data-lulu-attributes": JSON.stringify({
        type: "product",
        product: {
          categoryUnifiedID,
          color,
          deliveryType: "ship",
          name: formatNameTracking(name),
          price,
          productID,
          quantity: "",
          size: "",
          skuID,
          styleID: defaultSku.styleNumber,
        },
        attributes: {
          component,
          displayType,
          position,
        },
        version: 2.2,
      }),
      "data-categoryunifiedid": categoryUnifiedID,
      "data-productid": productID,
      "data-skuid": skuID,
    };
  } catch (e) {
    log.error(`productTileAnalyticsDataAttributes Error: ${e}`);
  }
  return {};
};

/**
 * Returns a value to populate data attributes for color swatches nested within
 * product tiles sourced from GraphQL.
 *
 * @param {Object} product
 * @param {Object} product.defaultSku - Default SKU for the product.
 * @param {string} product.href - The URL of the product's PDP.
 * @param {string} product.id - The product ID.
 * @param {string} product.name - The name of the product.
 * @param {Array} product.colors - The list of colors of the product.
 * @param {Number} index - The relative position of the link based on the list.
 * @param {Number} parentIndex - The relative position of the parent's link based on the list.
 * @param {Object} overrides - Overrides for the default values.
 *
 * @returns {Object}
 */
export const productTileSwatchAnalyticsDataAttributes = ({
  product,
  index = 0,
  parentIndex = 0,
  overrides = {},
}) => {
  try {
    if (!product) return {};

    const { component, displayType } = overrides;

    const { colors = [], defaultSku = {}, href, id: productID, name } = product;

    const isUSA = defaultSku.price.currency.code === "USD";
    const skuID = isUSA ? `us_${defaultSku.id}` : `ca_${defaultSku.id}`;
    const color = colors[index]?.swatch?.id;
    const categoryUnifiedID = href.split("/")[2] || "";
    const position = `${parentIndex + 1}-${index + 1}`;

    return {
      "data-lulu-attributes": JSON.stringify({
        type: "product",
        product: {
          categoryUnifiedID,
          color,
          deliveryType: "ship",
          name: formatNameTracking(name),
          productID,
          skuID,
        },
        attributes: {
          component: component || "product-list",
          displayType: displayType || "swatch",
          position,
        },
        version: 2.1,
      }),
      "data-categoryunifiedid": categoryUnifiedID,
      "data-productid": productID,
      "data-skuid": skuID,
    };
  } catch (e) {
    log.error(`productTileSwatchAnalyticsDataAttributes Error: ${e}`);
  }
  return {};
};

export function trackCarouselProductTileClick({
  carouselType,
  campaignId,
  collectionType,
  product,
  products,
}) {
  const styleOrder = product?.["sku-style-order"]?.[0];

  const countryPrefix = getLocalePrefix(product);
  const [sku, hasSku] = product?.id?.split?.("_") || [];
  const skuID = hasSku ? `${countryPrefix}${sku}` : "";
  mwaAnalyticsTrackEvent(EVENT_CATEGORY.PRODUCT_INTERACTION, {
    component: {
      id: campaignId,
      type: carouselType,
      text: "", // leave blank
      position: products.indexOf(product),
      totalCount: products.length,
    },
    product: {
      productID: product?.id || "",
      name: product?.["display-name"] || "",
      price: product?.["list-price"]?.[0] || "",
      salePrice: product?.["sale-price"] || "",
      isSale: (product?.["sale-price"] || []).length > 0,
      colorName: styleOrder?.["color-name"] || "",
      colorId: styleOrder?.["color-id"] || "",
      colorGroup: styleOrder?.["color-group"] || "",
      skuID,
      // the following props are not available
      categoryUnifiedID: "",
      unifiedID: "",
      localizedName: "",
    },
    collection: {
      type: collectionType || campaignId,
      id: campaignId,
    },
  });
}
